import moment from 'moment';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { from, Observable, of, Subject } from 'rxjs';
import { EventUserActiveReply, EventUserTyping, User } from '@shared/api/api-loop/models';
import { ContactModel } from '../../../shared/models-api-loop/contact/contact.model';
import {
  bufferTime,
  catchError,
  concatMap,
  delay,
  filter,
  map,
  mergeMap,
  repeatWhen,
  startWith,
  switchMap,
  tap,
  toArray
} from 'rxjs/operators';
import { AutoUnsubscribe } from '../../../shared/utils/subscriptions/auto-unsubscribe';
import { EventHubService } from '@shared/synchronization/websockets/event-hub.service';
import { SmartContactsApiService } from '@shared/modules/contacts/shell/contacts-api-cache/smart-contacts-api.service';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { HubEventType } from '@dta/shared/models/event.models';

////////////////
// Parameters
////////////////
const inactiveUserThresholdSeconds_ActiveReply = 3;
const inactiveUserThresholdSeconds_Typing = 4;

@AutoUnsubscribe()
@Injectable()
export abstract class WebsocketCardEventsAggregationService implements OnDestroy {
  protected readonly eventHubService: EventHubService = inject(EventHubService);
  protected readonly userAuthService: UserAuthService = inject(UserAuthService);
  protected readonly smartContactsApiService: SmartContactsApiService = inject(SmartContactsApiService);

  ///////////////////
  // State variables
  ///////////////////
  private currentActiveCards: WebsocketEventCard[] = [];
  private readonly inactiveUserThresholdSecond: number;

  /////////////
  // Subjects
  /////////////
  private readonly output$: Subject<WebsocketEventCard[]> = new Subject();

  constructor(private readonly _eventTypes: HubEventType[]) {
    this.inactiveUserThresholdSecond = _eventTypes.includes(HubEventType.EventUserActiveReply)
      ? inactiveUserThresholdSeconds_ActiveReply
      : inactiveUserThresholdSeconds_Typing;

    this.subscribeToEvents();
    this.subscribeToExpiredUsers();
  }

  ngOnDestroy() {}

  registerCard(cardId: string): Observable<WebsocketEventCard[]> {
    return this.output$.pipe(
      startWith(this.currentActiveCards),
      map((cards: WebsocketEventCard[]) => cards.filter(card => card.cardId === cardId)),
      filter((cards: WebsocketEventCard[]) => cards && cards.length > 0)
    );
  }

  protected getWebsocketCard(cardId: string): Observable<WebsocketEventCard[]> {
    return this.registerCard(cardId);
  }

  private subscribeToEvents(): void {
    this.userAuthService.currentSession$
      .pipe(
        switchMap(() =>
          this.eventHubService.getEvents$<WebsocketCardEvent>(this._eventTypes).pipe(
            bufferTime(100),
            filter(events => events && events.length > 0),
            concatMap(events => this.processCardWebsocketEvents(events))
          )
        )
      )
      .subscribe();
  }

  private subscribeToExpiredUsers(): void {
    of(null)
      .pipe(
        map(() => {
          const currentTime = moment();
          this.currentActiveCards = this.currentActiveCards.map(card => ({
            ...card,
            users: card.users.filter(
              user => currentTime.diff(user.timestamp, 'seconds') <= this.inactiveUserThresholdSecond
            )
          }));
          return this.currentActiveCards;
        }),
        tap((cards: WebsocketEventCard[]) => this.output$.next(cards)),
        repeatWhen((notifier: Observable<any>) => notifier.pipe(delay(3 * 1000)))
      )
      .subscribe();
  }

  private processCardWebsocketEvents(events: WebsocketCardEvent[]): Observable<WebsocketEventCard[]> {
    if (!events || events.length === 0) {
      return of([]);
    }

    return this.populateUsers(events).pipe(
      mergeMap(event => this.addUserToActiveCard(event)),
      toArray(),
      tap((cards: WebsocketEventCard[]) => this.output$.next(cards))
    );
  }

  private populateUsers(events: WebsocketCardEvent[]): Observable<WebsocketCardEvent> {
    return from(events).pipe(
      mergeMap((event: WebsocketCardEvent) =>
        this.smartContactsApiService.getSingle(event.user.id).pipe(
          map((contact: ContactModel) => {
            event.user = contact;
            return event;
          }),
          catchError(() => of(event))
        )
      )
    );
  }

  private addUserToActiveCard(event: WebsocketCardEvent): Observable<WebsocketEventCard> {
    if (!event) {
      return of(undefined);
    }

    return of(event).pipe(
      map((event: WebsocketCardEvent) => {
        let card = this.currentActiveCards.find(c => c.cardId === event.cardId);

        if (card) {
          card.users = [this.addCurrentTimestampToUser(event.user), ...card.users].filter(
            (user, index, self) => self.findIndex(u => u.id === user.id) === index
          );
          card.users = [...card.users].sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
        } else {
          card = {
            cardId: event.cardId,
            users: [this.addCurrentTimestampToUser(event.user)]
          };
          this.currentActiveCards.push(card);
        }

        return card;
      })
    );
  }

  private addCurrentTimestampToUser(user: User): UserWithActionOnCard {
    return Object.assign({ timestamp: moment() }, user);
  }
}

export type WebsocketCardEvent = EventUserTyping | EventUserActiveReply;

export interface WebsocketEventCard {
  cardId: string;
  users: UserWithActionOnCard[];
}

interface UserWithActionOnCard extends User {
  timestamp: any;
}
